import React from "react";
import { formatDistance } from "date-fns";
import { Pagination, ScanReportType } from "@sscs/types";
import {
  Button,
  DataTable,
  DataTableColumn,
  LongTextLabel,
  Table,
  useFilterSortPagination,
} from "@sscs/ui";
import { formatCount, getQueryStatus } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION, USER_REMOVED } from "Constants";
import { useAllowedTo, useCheckPaginationPage, useDialog } from "Hooks";
import { SharedReportStats, SharedToken } from "Types";
import { useGetSharedReportLinksQuery } from "../../../../services/sharedReports.api";
import { LinksTableFilter, LinksTableSort } from "../../../../types";
import { isSharedReportLinkExpired } from "../../../../utils";
import LinkOverviewDialog from "../../LinkOverviewDialog/LinkOverviewDialog";
import ManageInvitesDialog from "../../ManageInvitesDialog/ManageInvitesDialog";
import CopyLink from "./CopyLink";
import CopyPassword from "./CopyPassword";
import LinkActions from "./LinkActions";

const formatDateTimeDistance = (dateTime: string): string =>
  formatDistance(new Date(dateTime), new Date(), {
    addSuffix: true,
  });

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 3,
};

const initFilter: LinksTableFilter = {
  active_only: false,
};

const initSort: LinksTableSort = {
  property: "expiry",
  reverse: true,
};

type LinkRow = {
  link: SharedToken;
  purl: string;
  versionId: string;
  reportType?: ScanReportType;
};

const LinkRow = ({
  link,
  purl,
  versionId,
  reportType,
}: LinkRow): React.JSX.Element => {
  const linkOverviewDialog = useDialog();
  const resendLinkDialog = useDialog();

  const openResendLinkDialog = () => {
    linkOverviewDialog.close();
    resendLinkDialog.open();
  };

  return (
    <>
      <LinkOverviewDialog
        sharedReport={link}
        purl={purl}
        onClose={linkOverviewDialog.close}
        isOpen={linkOverviewDialog.isOpen}
        openResendLinkDialog={openResendLinkDialog}
      />
      <ManageInvitesDialog
        sharedReport={link}
        purl={purl}
        onClose={resendLinkDialog.close}
        isOpen={resendLinkDialog.isOpen}
      />
      <Table.Row>
        <Table.Cell>
          <Button onClick={linkOverviewDialog.open} variant="text" isInline>
            <LongTextLabel
              label={link.settings.label}
              maxCharacters={60}
              inline
            />
          </Button>
        </Table.Cell>
        <Table.Cell>
          {link.user
            ? `${link.user.first_name} ${link.user.last_name}`
            : USER_REMOVED}
        </Table.Cell>
        <Table.Cell>
          {isSharedReportLinkExpired(link) ? (
            "-"
          ) : (
            <CopyLink token={link.token} />
          )}
        </Table.Cell>
        <Table.Cell>
          {isSharedReportLinkExpired(link) ? (
            "-"
          ) : (
            <CopyPassword password={link.password} />
          )}
        </Table.Cell>
        <Table.Cell>
          {formatDateTimeDistance(link.settings.expiration_date)}
        </Table.Cell>
        <Table.Cell>
          {link.recipients.count > 0
            ? `${link.recipients.viewed_report.toLocaleString()} of ${link.recipients.count.toLocaleString()} Viewed`
            : "N/A"}
        </Table.Cell>
        <AllowedTo permissions={[PERMISSION.SHARE_REPORT]}>
          <Table.Cell>
            <LinkActions
              link={link}
              purl={purl}
              versionId={versionId}
              reportType={reportType}
            />
          </Table.Cell>
        </AllowedTo>
      </Table.Row>
    </>
  );
};

type LinksTableProps = {
  versionId: string;
  purl: string;
  reportType?: ScanReportType;
  stats: SharedReportStats | null;
  openShareReportDialog: () => void;
};

const LinksTable = ({
  versionId,
  purl,
  reportType,
  stats,
  openShareReportDialog,
}: LinksTableProps): React.JSX.Element => {
  const { filter, sort, pagination, handleSortChange, handlePaginationChange } =
    useFilterSortPagination(initFilter, initSort, initPagination);

  const { data, isLoading, isFetching, isError, error } =
    useGetSharedReportLinksQuery({
      version_id: versionId,
      report_type: reportType,
      offset: (pagination.currentPage - 1) * pagination.itemsPerPage,
      limit: pagination.itemsPerPage,
      filter,
      sort,
    });

  useCheckPaginationPage({
    matchCount: data?.match_count,
    itemLength: data?.shared_reports.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange,
  });

  const canUpdateReportLink = useAllowedTo({
    permissions: [PERMISSION.SHARE_REPORT],
  });

  const columns: DataTableColumn<LinksTableSort["property"]>[] = [
    {
      id: "label",
      label: "Link name",
      sortProperty: "label",
      width: "100%",
      minWidth: "130px",
    },
    { id: "user", label: "Shared by", sortProperty: "user", width: "300px" },
    { id: "link", label: "Link", width: "200px" },
    { id: "password", label: "Password", width: "100px" },
    {
      id: "expiry",
      label: "Expiration",
      sortProperty: "expiry",
      width: "180px",
    },
    { id: "recipients", label: "Recipients", width: "180px" },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      width: "90px",
      hidden: !canUpdateReportLink,
    },
  ];

  return (
    <DataTable
      title={stats ? "Shared links" : undefined}
      titleDetails={
        stats
          ? [
              formatCount(stats.token_count, "Shared link"),
              <span key="views">
                <b>Report views:</b> {stats.view_count.toLocaleString()}
              </span>,
              <span key="first-shared">
                <b>First shared:</b>{" "}
                {stats.first_shared
                  ? formatDateTimeDistance(stats.first_shared)
                  : "-"}
              </span>,
              <span key="last-viewed">
                <b>Last viewed:</b>{" "}
                {stats.last_opened
                  ? formatDateTimeDistance(stats.last_opened)
                  : "-"}
              </span>,
              <span key="sbom-downloads">
                <b>SBOM downloads:</b>{" "}
                {stats.sbom_export_count.toLocaleString()}
              </span>,
              <span key="package-downloads">
                <b>Package downloads:</b>{" "}
                {stats.package_download_count.toLocaleString()}
              </span>,
            ]
          : undefined
      }
      titleActions={
        <AllowedTo permissions={[PERMISSION.SHARE_REPORT]}>
          <Button type="button" color="primary" onClick={openShareReportDialog}>
            Share report
          </Button>
        </AllowedTo>
      }
      columns={columns}
      sort={sort}
      onSortChange={handleSortChange}
      empty={{ subject: "shared report links" }}
      pagination={{
        itemCount: data?.match_count ?? 0,
        pagination,
        onChange: handlePaginationChange,
        initialItemCount: initPagination.itemsPerPage,
      }}
      status={getQueryStatus({
        isLoading,
        isFetching,
        isError,
        error,
        data,
      })}
    >
      {data?.shared_reports.map((reportLink) => (
        <LinkRow
          key={reportLink.token}
          link={reportLink}
          purl={purl}
          versionId={versionId}
          reportType={reportType}
        />
      ))}
    </DataTable>
  );
};

export default LinksTable;
